import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';

const Home = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [currentScreenshot, setCurrentScreenshot] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState('');
  const [modalImageAlt, setModalImageAlt] = useState('');
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  useEffect(() => {
    // Verifica se há uma âncora na URL e faz scroll para ela
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      const element = document.getElementById(sectionId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }, 100);
      }
    }
  }, [location.hash]);

  // Atualiza o título da página com a tradução atual
  useEffect(() => {
    const title = `Jobya - ${t('slogan')}`;
    if (title) document.title = title;
  }, [t, i18n.language]);

  const features = [
    {
      title: t('features.contractors.title'),
      description: t('features.contractors.description'),
      image: '/images/contratante.png'
    },
    {
      title: t('features.professionals.title'),
      description: t('features.professionals.description'),
      image: '/images/contratado.png'
    }
  ];

  const screenshots = Array.from({ length: 9 }, (_, i) =>
    `/images/screenshots/jobya_${i + 1}.png`
  );

  // Carrossel automático de screenshots - rola horizontalmente
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentScreenshot((prev) => prev + 1);
    }, 4000); // Muda a cada 4 segundos

    return () => clearInterval(interval);
  }, []);

  // Fechar modal com tecla Escape e navegar com setas
  useEffect(() => {
    const handleKeydown = (event) => {
      if (!isModalOpen) return;
      
      switch (event.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowLeft':
          goToPreviousModal();
          break;
        case 'ArrowRight':
          goToNextModal();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isModalOpen, modalImageIndex]);

  // Funções para navegação manual com setas
  const goToPrevious = () => {
    setCurrentScreenshot((prev) => prev - 1);
  };

  const goToNext = () => {
    setCurrentScreenshot((prev) => prev + 1);
  };

  // Funções para o modal de imagem
  const openModal = (imageSrc, imageAlt, imageIndex) => {
    setModalImageSrc(imageSrc);
    setModalImageAlt(imageAlt);
    setModalImageIndex(imageIndex);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden'; // Previne scroll do body
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImageSrc('');
    setModalImageAlt('');
    setModalImageIndex(0);
    document.body.style.overflow = 'unset'; // Restaura scroll do body
  };

  // Funções para navegação no modal
  const goToPreviousModal = () => {
    const newIndex = modalImageIndex > 0 ? modalImageIndex - 1 : screenshots.length - 1;
    setModalImageIndex(newIndex);
    setModalImageSrc(screenshots[newIndex]);
    setModalImageAlt(`Screenshot ${newIndex + 1}`);
  };

  const goToNextModal = () => {
    const newIndex = modalImageIndex < screenshots.length - 1 ? modalImageIndex + 1 : 0;
    setModalImageIndex(newIndex);
    setModalImageSrc(screenshots[newIndex]);
    setModalImageAlt(`Screenshot ${newIndex + 1}`);
  };

  // Funções para gestos de swipe
  const handleTouchStart = (e) => {
    setTouchEndX(null);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    
    const distance = touchStartX - touchEndX;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNextModal();
    }
    if (isRightSwipe) {
      goToPreviousModal();
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="container">
          <div className="hero-inner">
            <div className="hero-logo">
              <img src="/images/logo.png" alt="Jobya logo" />
            </div>

            <div className="hero-content">
              <h2>{t('slogan')}</h2>
              <p className="hero-description">{t('hero.description')}</p>
              <div className="hero-buttons">
                <p className="hero-promo">{t('hero.promo')}</p>

                <div className="app-links">
                  <a
                    href="https://apps.apple.com/app/jobya-pro/id6747011474"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-link"
                  >
                    <img src="/images/storebadge/appstore_en.svg"
                      alt="Download on App Store"
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=br.com.jobyapro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-link"
                  >
                    <img src="/images/storebadge/googleplay_en.png"
                      alt="Get it on Google Play" />
                  </a>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section">
        <div className="container">
          <h2>{t('features.title')}</h2>

          <div className="benefits">
            <ul>
              <li>{t('benefits.profile')}</li>
              <li>{t('benefits.searchFilters')}</li>
              <li>{t('benefits.postOffers')}</li>
              <li>{t('benefits.realTimeNotifications')}</li>
              <li>{t('benefits.directContact')}</li>
              <li>{t('benefits.historyReviews')}</li>
            </ul>
          </div>

          <div className="features-carousel">
            <div className="features-track">
              {/* Duplicando os cards para o efeito infinito */}
              {[...features].map((feature, index) => (
                <div key={index} className="feature-card">
                  <img src={feature.image} alt={feature.title} />
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section id="screenshots" className="section" style={{ backgroundColor: 'var(--card-bg)' }}>
        <div className="container">
          <h2>{t('screenshots.title')}</h2>
          <div className="screenshots-carousel-container">
            <button className="carousel-arrow carousel-arrow-left" onClick={goToPrevious}>
              &#8249;
            </button>
            <div className="screenshots-carousel-new">
              <div 
                className="screenshots-track-new"
                style={{
                  transform: `translateX(-${(currentScreenshot % screenshots.length) * 220}px)`,
                }}
              >
                {/* Duplicando screenshots para efeito contínuo */}
                {[...screenshots, ...screenshots, ...screenshots].map((screenshot, index) => {
                  const originalIndex = index % screenshots.length;
                  return (
                    <img
                      key={index}
                      src={screenshot}
                      alt={`Screenshot ${originalIndex + 1}`}
                      className="screenshot-new"
                      onClick={() => openModal(screenshot, `Screenshot ${originalIndex + 1}`, originalIndex)}
                      style={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </div>
            </div>
            <button className="carousel-arrow carousel-arrow-right" onClick={goToNext}>
              &#8250;
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="container">
          <h2>{t('contact.title')}</h2>
          <ContactForm />
        </div>
      </section>

      {/* Modal de visualização da imagem */}
      {isModalOpen && (
        <div className="image-modal-overlay" onClick={closeModal}>
          <div 
            className="image-modal-content" 
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <button className="image-modal-close" onClick={closeModal}>
              &times;
            </button>
            
            <button className="modal-arrow modal-arrow-left" onClick={goToPreviousModal}>
              &#8249;
            </button>
            
            <img 
              src={modalImageSrc} 
              alt={modalImageAlt} 
              className="image-modal-img"
              draggable={false}
              style={{ userSelect: 'none' }}
            />
            
            <button className="modal-arrow modal-arrow-right" onClick={goToNextModal}>
              &#8250;
            </button>
            
            <div className="modal-image-counter">
              {modalImageIndex + 1} / {screenshots.length}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Home;
